/**
 * Campaign-aware API binding.
 *
 * Each campaign targets its own backend module (separate endpoints), so the
 * generated react-query hooks differ per campaign. This module exposes a single
 * `campaignApi` object pointing to the hooks of the active campaign, so the form
 * components stay campaign-agnostic.
 */
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import {
  useSd500Emd500eControllerCreateEmailConfirmation,
  useSd500Emd500eControllerCreateOwnerChanged,
  useSd500Emd500eControllerCreateUnknownNewOwner,
  useSd500Emd500eControllerCreateProductQuantity,
  useSd500Emd500eControllerCreateKitRequest,
} from '../api/generated'
import { activeCampaignSlug } from './active'

/**
 * Loose mutation hook signature shared by every campaign.
 * Payloads differ slightly between products (quantity shape), so `data` is kept
 * generic; each form builds its own typed payload locally.
 */
export type CampaignMutationHook = (options?: {
  mutation?: UseMutationOptions<any, any, { data: any }, any>
}) => UseMutationResult<any, any, { data: any }, any>

/**
 * Set of mutation hooks used across the recall wizard for a single campaign.
 */
export interface CampaignApi {
  useCreateEmailConfirmation: CampaignMutationHook
  useCreateOwnerChanged: CampaignMutationHook
  useCreateUnknownNewOwner: CampaignMutationHook
  useCreateQuantity: CampaignMutationHook
  useCreateKitRequest: CampaignMutationHook
}

const apiByCampaign: Record<string, CampaignApi> = {
  'sd500-emd500e': {
    useCreateEmailConfirmation:
      useSd500Emd500eControllerCreateEmailConfirmation as CampaignMutationHook,
    useCreateOwnerChanged: useSd500Emd500eControllerCreateOwnerChanged as CampaignMutationHook,
    useCreateUnknownNewOwner:
      useSd500Emd500eControllerCreateUnknownNewOwner as CampaignMutationHook,
    useCreateQuantity: useSd500Emd500eControllerCreateProductQuantity as CampaignMutationHook,
    useCreateKitRequest: useSd500Emd500eControllerCreateKitRequest as CampaignMutationHook,
  },
}

/**
 * Stand-in for campaigns with no backend binding.
 *
 * Throws on first use rather than at import time. Campaigns that ship their own
 * screens never touch these hooks, yet this module is still pulled in
 * transitively through the campaign registry — throwing on import would stop
 * them booting at all. Wizard campaigns still fail fast, just at the point of
 * use rather than at load, so a misconfigured CAMPAIGN can never silently fall
 * back to another campaign's endpoints.
 */
const missingCampaignApi = (): CampaignApi =>
  new Proxy({} as CampaignApi, {
    get(_target, property) {
      throw new Error(
        `Campaign "${activeCampaignSlug ?? 'undefined'}" has no API binding, but ` +
          `${String(property)} was used. Known bindings: ${Object.keys(apiByCampaign).join(', ')}`,
      )
    },
  })

/**
 * API hooks for the active campaign.
 */
export const campaignApi: CampaignApi =
  (activeCampaignSlug && apiByCampaign[activeCampaignSlug]) || missingCampaignApi()
